/** @format */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Grid,
    Typography,
    FormControl,
    Select,
    MenuItem as MuiMenuItem,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchCollectionsMetrics,
    fetchRecoveryFunnel,
    fetchRecoveryHealth,
    fetchRecoveryTrends,
    fetchDashboardOverview,
    fetchBusinessPromises,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectCollectionsMetrics,
    selectIsLoadingCollectionsMetrics,
    selectRecoveryFunnel,
    selectIsLoadingRecoveryFunnel,
    selectRecoveryHealth,
    selectIsLoadingRecoveryHealth,
    selectRecoveryTrends,
    selectIsLoadingRecoveryTrends,
    selectDashboardOverview,
    selectIsLoadingDashboardOverview,
    selectBusinessPromises,
    selectIsLoadingBusinessPromises,
} from "../../redux/selectors/collectSelector";
import { CollectionsRange } from "../../utility/types/collectTypes";
import { colors } from "../../utility/types/constant";
import RelationshipSafetyPanel from "./dashboard/RelationshipSafetyPanel";
import CollectionsBarChart from "./dashboard/CollectionsBarChart";
import RecoveryFunnelPanel from "./dashboard/RecoveryFunnelPanel";
import RecoveryHealthPanel from "./dashboard/RecoveryHealthPanel";
import RecoveryTrendsPanel from "./dashboard/RecoveryTrendsPanel";
import UrgentInvoicesPanel from "./dashboard/UrgentInvoicesPanel";
import UrgencyChart from "./dashboard/UrgencyChart";
import PromiseToPayPanel from "./dashboard/PromiseToPayPanel";
import WorkflowFlowDiagram from "./dashboard/WorkflowFlowDiagram";
import {
    CollectPageWrapper,
    CollectGhostButton,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectAnalytics — the "deeper details" workspace.
//
// These panels used to live on the main dashboard. They were moved here so
// the dashboard can lead with the three answers an owner needs at a glance
// (recovered / working / needs me), while the richer breakdowns stay one
// click away for anyone who wants to dig in. No panel was removed — every
// chart that was on the dashboard is rendered below.
// ─────────────────────────────────────────────────────────────────────────

const CollectAnalytics: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const activeBusinessId = useSelector(selectActiveBusinessId);

    const collectionsMetrics = useSelector(selectCollectionsMetrics);
    const isLoadingCollectionsMetrics = useSelector(
        selectIsLoadingCollectionsMetrics,
    );
    const recoveryFunnel = useSelector(selectRecoveryFunnel);
    const isLoadingRecoveryFunnel = useSelector(selectIsLoadingRecoveryFunnel);
    const recoveryHealth = useSelector(selectRecoveryHealth);
    const isLoadingRecoveryHealth = useSelector(selectIsLoadingRecoveryHealth);
    const recoveryTrends = useSelector(selectRecoveryTrends);
    const isLoadingRecoveryTrends = useSelector(selectIsLoadingRecoveryTrends);
    const dashboardOverview = useSelector(selectDashboardOverview);
    const isLoadingDashboardOverview = useSelector(
        selectIsLoadingDashboardOverview,
    );
    const businessPromises = useSelector(selectBusinessPromises);
    const isLoadingBusinessPromises = useSelector(
        selectIsLoadingBusinessPromises,
    );

    // ── Range picker for collections metrics ──────────────────────────────
    const [metricsRange, setMetricsRange] = useState<CollectionsRange>("4w");
    // ── Range picker for the recovery trends time-series ──────────────────
    const [trendsDays, setTrendsDays] = useState<number>(30);

    // Always open this page scrolled to the top — React Router preserves the
    // window scroll position across navigations, so coming from a long
    // dashboard would otherwise drop the user into the middle of the page.
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, []);

    // Load the analytics datasets once a business is active.
    useEffect(() => {
        if (!activeBusinessId) return;
        dispatch(fetchRecoveryFunnel(undefined));
        dispatch(fetchRecoveryHealth(undefined));
        dispatch(fetchDashboardOverview({ limit: 12 }));
        dispatch(fetchBusinessPromises({ status: "pending", limit: 25 }));
    }, [dispatch, activeBusinessId]);

    // Collections metrics react to the period selector.
    useEffect(() => {
        if (!activeBusinessId) return;
        dispatch(fetchCollectionsMetrics({ range: metricsRange }));
    }, [dispatch, activeBusinessId, metricsRange]);

    // Recovery trends react to the days selector.
    useEffect(() => {
        if (!activeBusinessId) return;
        dispatch(fetchRecoveryTrends({ days: trendsDays }));
    }, [dispatch, activeBusinessId, trendsDays]);

    return (
        <CollectPageWrapper>
            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <FlexBetween style={{ marginBottom: 16 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                color: colors.primary,
                            }}
                        >
                            Analytics &amp; details
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#6b7280",
                                fontSize: "0.9rem",
                                mt: 0.5,
                                maxWidth: 720,
                            }}
                        >
                            The deeper breakdowns behind your dashboard collections
                            trends, the recovery funnel, relationship safety, promises
                            and the live workflow. Your dashboard stays focused on what
                            you recovered and what needs you; this is where you dig in.
                        </Typography>
                    </Box>
                    <CollectGhostButton
                        startIcon={<ArrowBack fontSize="small" />}
                        onClick={() => navigate("/collect/dashboard")}
                    >
                        Back to dashboard
                    </CollectGhostButton>
                </FlexBetween>

                {/* ─── Period selector for collections metrics ─────────── */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        mb: 1.5,
                        gap: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: "#6b7280",
                        }}
                    >
                        Period:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={metricsRange}
                            onChange={(e) =>
                                setMetricsRange(e.target.value as CollectionsRange)
                            }
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                borderRadius: "10px",
                                "& .MuiSelect-select": { py: 0.85 },
                            }}
                        >
                            <MuiMenuItem value="2w">Last 2 weeks</MuiMenuItem>
                            <MuiMenuItem value="4w">Last 4 weeks</MuiMenuItem>
                            <MuiMenuItem value="3m">Last 3 months</MuiMenuItem>
                            <MuiMenuItem value="6m">Last 6 months</MuiMenuItem>
                            <MuiMenuItem value="12m">Last 12 months</MuiMenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* ─── Relationship safety ─────────────────────────────── */}
                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <RelationshipSafetyPanel businessId={activeBusinessId} />
                </Box>

                {/* ─── Collections bar chart ───────────────────────────── */}
                <Box sx={{ mb: 3 }}>
                    <CollectionsBarChart
                        metrics={collectionsMetrics}
                        isLoading={isLoadingCollectionsMetrics}
                    />
                </Box>

                {/* ─── Recovery funnel + collections health ────────────── */}
                <Grid container spacing={2} sx={{ marginBottom: "16px" }}>
                    <Grid item xs={12} lg={7}>
                        <RecoveryFunnelPanel
                            funnel={recoveryFunnel}
                            isLoading={isLoadingRecoveryFunnel}
                        />
                    </Grid>
                    <Grid item xs={12} lg={5}>
                        <RecoveryHealthPanel
                            health={recoveryHealth}
                            isLoading={isLoadingRecoveryHealth}
                        />
                    </Grid>
                </Grid>

                {/* ─── Recovery trends (time-series from daily snapshots) ── */}
                <Box sx={{ mb: 3 }}>
                    <RecoveryTrendsPanel
                        trends={recoveryTrends}
                        isLoading={isLoadingRecoveryTrends}
                        days={trendsDays}
                        onDaysChange={setTrendsDays}
                    />
                </Box>

                {/* ─── Urgent invoices + urgency + promises + workflow ──── */}
                <Grid container spacing={2} sx={{ marginBottom: "16px" }}>
                    <Grid item xs={12} lg={8}>
                        <UrgentInvoicesPanel
                            overview={dashboardOverview}
                            isLoading={isLoadingDashboardOverview}
                            onRefresh={() =>
                                dispatch(fetchDashboardOverview({ limit: 12 }))
                            }
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <UrgencyChart
                            overview={dashboardOverview}
                            isLoading={isLoadingDashboardOverview}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <PromiseToPayPanel
                            promises={businessPromises}
                            isLoading={isLoadingBusinessPromises}
                            onRefresh={() =>
                                dispatch(
                                    fetchBusinessPromises({
                                        status: "pending",
                                        limit: 25,
                                    }),
                                )
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <WorkflowFlowDiagram
                            overview={dashboardOverview}
                            isLoading={isLoadingDashboardOverview}
                        />
                    </Grid>
                </Grid>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectAnalytics;
